import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type PeriodoLetivoStatus = Database['public']['Enums']['periodo_letivo_status'];
export type PeriodoLetivoTipo = Database['public']['Enums']['periodo_letivo_tipo'];

/** Linha tipada da tabela de períodos letivos. */
export type PeriodoLetivoRow = Database['public']['Tables']['periodos_letivos']['Row'];
export type PeriodoLetivoInsert = Database['public']['Tables']['periodos_letivos']['Insert'];
export type PeriodoLetivoUpdate = Database['public']['Tables']['periodos_letivos']['Update'];

/**
 * Período letivo enriquecido para o frontend.
 *
 * Estende a Row do Supabase com campos derivados de joins (nome da unidade
 * e nome do próximo período na cadeia de evolução).
 */
export interface PeriodoLetivo extends PeriodoLetivoRow {
  unidade_nome?: string | null;
  proximo_periodo_nome?: string | null;
}

export type PeriodosLetivosFiltros = {
  unidadeId?: string | null;
  status?: PeriodoLetivoStatus | null;
  ativo?: boolean | null;
  /** Quando true, filtra apenas períodos sem unidade escolar (Geral). */
  apenasGeral?: boolean;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normaliza o argumento do hook (string ou objeto) num único objeto de filtros. */
const normalizarFiltros = (
  entrada?: PeriodosLetivosFiltros | string | null,
): PeriodosLetivosFiltros | undefined => {
  if (entrada == null) return undefined;
  if (typeof entrada === 'string') return entrada ? { unidadeId: entrada } : undefined;
  return entrada;
};

// ─── Query principal (somente leitura) ───────────────────────────────────────

export const usePeriodosLetivos = (
  filtrosOuUnidade?: PeriodosLetivosFiltros | string | null,
  opcoes?: { enabled?: boolean },
) => {
  const filtros = normalizarFiltros(filtrosOuUnidade);

  return useQuery<PeriodoLetivo[]>({
    queryKey: ['periodos_letivos', filtros ?? null],
    enabled: opcoes?.enabled ?? true,
    queryFn: async () => {
      let query = (supabase as any)
        .from('periodos_letivos')
        .select(`
          *,
          unidade_escolar:unidade_escolar_id(nome),
          proximo:periodos_letivos!proximo_periodo_id(nome)
        `)
        .order('data_inicio', { ascending: false });

      if (filtros?.apenasGeral) {
        query = query.is('unidade_escolar_id', null);
      } else if (filtros?.unidadeId) {
        query = query.eq('unidade_escolar_id', filtros.unidadeId);
      }
      if (filtros?.status) {
        query = query.eq('status_periodo', filtros.status);
      }
      if (filtros?.ativo === true || filtros?.ativo === false) {
        query = query.eq('ativo', filtros.ativo);
      }

      const { data, error } = await query;
      if (error) throw error;

      type LinhaComEmbeds = PeriodoLetivoRow & {
        unidade_escolar?: { nome: string | null } | null;
        proximo?: { nome: string | null } | null;
      };

      return (data ?? []).map((item: unknown) => {
        const { unidade_escolar, proximo, ...row } = item as LinhaComEmbeds;
        return {
          ...row,
          unidade_nome: unidade_escolar?.nome ?? null,
          proximo_periodo_nome: proximo?.nome ?? null,
        } as PeriodoLetivo;
      });
    },
  });
};

// ─── Re-export compartilhado ─────────────────────────────────────────────────
// Componentes legados (NovaMandatoDrawer, CalendarioCursoDashboard) importavam
// `useCursos` daqui; mantemos o alias para evitar regressões.
export { useCursosModulo as useCursos } from './useCursos';
